import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

/* ─────────────── Leaderboard — mass-poster skin ───────────────
   Ink canvas with an amber rail up top and red accents. Zero gold glow,
   zero rounded pills; ranking table reads as a stat sheet from a fight
   program. */

// ────────── layout ──────────
const LeaderboardContainer = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(ellipse 70% 50% at 50% -10%, rgba(255, 177, 60, 0.05) 0%, transparent 70%),
    var(--color-ink-950);
  padding: clamp(24px, 5vh, 56px) var(--space-6);
  color: var(--color-cream-100);
  position: relative;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: var(--space-10);
`;

/* Display-italic title with flanking slashes — same language as the
   VICTORY/DEFEAT card so the two pages feel authored, not templated. */
const Title = styled.h1`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: 0 0 var(--space-2);
  font-family: var(--font-display);
  font-size: clamp(40px, 5vw, 68px);
  font-weight: var(--weight-bold);
  font-style: italic;
  letter-spacing: -0.01em;
  line-height: 0.95;
  text-transform: uppercase;
  color: var(--color-amber-500);
  text-shadow: 0 3px 0 var(--color-ink-950), 0 0 32px rgba(255, 177, 60, 0.3);

  &::before, &::after {
    content: '////';
    font-size: 0.35em;
    letter-spacing: -0.08em;
    opacity: 0.8;
  }
`;

const Subtitle = styled.p`
  font-family: var(--font-heading);
  font-size: 12px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-300);
  margin-bottom: var(--space-4);
`;

const RefreshButton = styled.button`
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  border: none;
  padding: 10px 22px;
  font-family: var(--font-heading);
  font-size: 12px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  cursor: pointer;
  clip-path: var(--clip-button);
  transition: filter var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);
  box-shadow: var(--shadow-md);

  &:hover { filter: brightness(1.08); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

// ────────── table ──────────
const LeaderboardTable = styled.div`
  max-width: 1020px;
  margin: 0 auto;
  position: relative;
  background: linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  box-shadow: var(--shadow-lg);
  overflow: hidden;

  /* amber top rail */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--color-amber-500);
  }
`;

const TableHeader = styled.div`
  background: rgba(8, 7, 10, 0.6);
  border-bottom: 1px solid var(--color-ink-700);
  padding: 16px 20px;
  display: grid;
  grid-template-columns: 80px 1fr 120px 90px 90px 120px;
  gap: 16px;
  align-items: center;
  font-family: var(--font-heading);
  font-weight: var(--weight-bold);
  font-size: 11px;
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-300);
`;

const TableBody = styled.div`
  max-height: 640px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: var(--color-ink-900); }
  &::-webkit-scrollbar-thumb {
    background: var(--color-ink-600);
    &:hover { background: var(--color-amber-500); }
  }
`;

const PlayerRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 80px 1fr 120px 90px 90px 120px;
  gap: 16px;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid var(--color-ink-800);
  cursor: pointer;
  position: relative;
  transition: background var(--dur-fast) var(--ease-out);

  &:hover {
    background: rgba(255, 177, 60, 0.06);
  }

  &:last-child { border-bottom: none; }

  /* Podium rows get a thin amber left stripe so the top 3 pop at a glance */
  &[data-rank="1"], &[data-rank="2"], &[data-rank="3"] {
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--color-amber-500);
    }
  }
`;

/* Rank display — Anton italic for podium positions, mono for the rest.
   Gold/silver/bronze medals replaced with amber/cream/red-400 — still
   three distinct colors, but inside the mass-poster palette. */
const Rank = styled.div`
  font-family: ${p => p.rank <= 3 ? 'var(--font-display)' : 'var(--font-mono)'};
  font-style: ${p => p.rank <= 3 ? 'italic' : 'normal'};
  font-size: ${p => p.rank <= 3 ? '28px' : '16px'};
  font-weight: var(--weight-bold);
  color: ${p => {
        if (p.rank === 1) return 'var(--color-amber-500)';
        if (p.rank === 2) return 'var(--color-cream-100)';
        if (p.rank === 3) return 'var(--color-red-400)';
        return 'var(--color-cream-300)';
    }};
  text-align: center;
  letter-spacing: ${p => p.rank <= 3 ? '-0.02em' : '0'};
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-heading);
  font-size: 14px;
  font-weight: var(--weight-bold);
  letter-spacing: 0.05em;
  clip-path: var(--clip-button);
  box-shadow: var(--shadow-sm);
`;

const PlayerName = styled.div`
  font-family: var(--font-heading);
  font-size: 15px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-cream-100);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatValue = styled.div`
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: var(--weight-semibold);
  color: var(--color-cream-200);
  text-align: center;
  letter-spacing: 0.02em;
`;

const Rating = styled(StatValue)`
  color: var(--color-amber-500);
  font-weight: var(--weight-bold);
  font-size: 16px;
`;

const WinRate = styled(StatValue)`
  color: ${p => {
        if (p.rate >= 70) return 'var(--color-success)';
        if (p.rate >= 50) return 'var(--color-amber-500)';
        return 'var(--color-red-500)';
    }};
  font-weight: var(--weight-bold);
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-cream-300);
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: var(--space-6);
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--color-red-500);
`;

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('http://localhost:3010/api/leaderboard');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setLeaderboard(data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (username) => {
    return username
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const calculateWinRate = (wins, losses) => {
    const total = wins + losses;
    return total > 0 ? Math.round((wins / total) * 100) : 0;
  };

  if (loading) {
    return (
      <LeaderboardContainer>
        <Header>
          <Title>Leaderboard</Title>
          <Subtitle>Battle Rankings ////</Subtitle>
        </Header>
        <LoadingSpinner>Loading leaderboard…</LoadingSpinner>
      </LeaderboardContainer>
    );
  }

  if (error) {
    return (
      <LeaderboardContainer>
        <Header>
          <Title>Leaderboard</Title>
          <Subtitle>Battle Rankings ////</Subtitle>
        </Header>
        <ErrorMessage>{error}</ErrorMessage>
        <div style={{ textAlign: 'center' }}>
          <RefreshButton onClick={fetchLeaderboard}>
            Try Again
          </RefreshButton>
        </div>
      </LeaderboardContainer>
    );
  }

  return (
    <LeaderboardContainer>
      <Header>
        <Title>Leaderboard</Title>
        <Subtitle>Battle Rankings ////</Subtitle>
        <RefreshButton onClick={fetchLeaderboard}>
          Refresh
        </RefreshButton>
      </Header>

      <LeaderboardTable>
        <TableHeader>
          <div style={{ textAlign: 'center' }}>Rank</div>
          <div>Player</div>
          <div style={{ textAlign: 'center' }}>Rating</div>
          <div style={{ textAlign: 'center' }}>Wins</div>
          <div style={{ textAlign: 'center' }}>Losses</div>
          <div style={{ textAlign: 'center' }}>Win Rate</div>
        </TableHeader>

        <TableBody>
          {leaderboard.map((player, index) => (
            <PlayerRow
              key={player._id}
              data-rank={player.rank}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.04, 0.4), duration: 0.25 }}
            >
              <Rank rank={player.rank}>
                {player.rank}
              </Rank>

              <PlayerInfo>
                <Avatar>
                  {getInitials(player.username)}
                </Avatar>
                <PlayerName>{player.username}</PlayerName>
              </PlayerInfo>

              <Rating>{player.rating}</Rating>
              <StatValue>{player.wins}</StatValue>
              <StatValue>{player.losses}</StatValue>
              <WinRate rate={calculateWinRate(player.wins, player.losses)}>
                {calculateWinRate(player.wins, player.losses)}%
              </WinRate>
            </PlayerRow>
          ))}
        </TableBody>
      </LeaderboardTable>
    </LeaderboardContainer>
  );
};

export default Leaderboard;
